export interface PdfLoadInstruction {
  type: 'load';
  debug?: boolean;
  data: Uint8Array<ArrayBufferLike>;
}

export interface PdfRasterizeInstructionPage {
  data: Uint8Array<ArrayBufferLike>;
  width: number;
  height: number;
}

export interface PdfRasterizeInstruction {
  type: 'rasterize';
  pages: PdfRasterizeInstructionPage[];
}

export interface PdfMergeInstructionImageItem {
  type: 'image';
  group: string;
  filename: string;
  content_type: string;
  data: Uint8Array<ArrayBufferLike>;
}

export interface PdfMergeInstructionPdfItem {
  type: 'pdf';
  title: string;
  filename: string;
  data: Uint8Array<ArrayBufferLike>;
  start_page: number;
  end_page: number;
  readonly: boolean;
  remove_fields?: string[];
  right_signature_placeholders?: {
    value: string;
    page_index: number;
    x: number;
    y: number;
    size: number;
  }[];
}

export type PdfMergeInstructionItem =
  PdfMergeInstructionPdfItem | PdfMergeInstructionImageItem;

export interface PdfMergeInstruction {
  type: 'merge';
  resize_to_letter: boolean;
  debug?: boolean;
  title: string;
  author: string;
  items: PdfMergeInstructionItem[];
}

export interface PdfEmbedFile {
  data: Uint8Array<ArrayBufferLike>;
  title: string;
  filename: string;
  content_type: string;
}

export type PdfInstruction =
  PdfLoadInstruction | PdfMergeInstruction | PdfRasterizeInstruction;

export async function processPdfInstruction(instruction: PdfInstruction) {
  const worker = new Worker(new URL('./pdf-worker.ts', import.meta.url), {
    type: 'module'
  });
  await new Promise<MessageEvent>(resolve =>
    worker.addEventListener('message', resolve, { once: true })
  );
  worker.postMessage(instruction);
  const event = await new Promise<
    MessageEvent<
      | { type: 'pdf-error'; message: string }
      | { type: 'pdf-data'; data: Uint8Array<ArrayBuffer> }
    >
  >(resolve => worker.addEventListener('message', resolve, { once: true }));
  worker.terminate();
  if (event.data.type === 'pdf-error') {
    throw new PdfProcessingError(event.data.message);
  }
  return event.data.data;
}

export class PdfProcessingError extends Error {
  constructor(public readonly userMessage: string) {
    super(`Error processing PDF instruction: ${userMessage}`);
    this.name = this.constructor.name;
    if (typeof Error.captureStackTrace === 'function') {
      Error.captureStackTrace(this, this.constructor);
    }
  }
}
